import { useEffect, useRef, useState } from "react";
import { AlertTriangle, Activity, Cloud, Globe } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { statusApi } from "../../api/status";
import { EmptyState, LoadingText, Pagination, Select } from "../ui";
import { SeverityBadge } from "./badges";
import { timeAgo, formatDateTime } from "./statusFormat";
import type { HealthEvent, HealthEventPage } from "../../types/health";
import type { Platform } from "../../types/status";

const SOURCE_META: Record<string, { label: string; Icon: LucideIcon; cls: string }> = {
  cloudwatch: { label: "CloudWatch", Icon: Cloud, cls: "text-blue-600" },
  signoz: { label: "SigNoz", Icon: AlertTriangle, cls: "text-orange-500" },
  uptimerobot: { label: "UptimeRobot", Icon: Activity, cls: "text-cyan-600" },
  url: { label: "URL check", Icon: Globe, cls: "text-cyan-600" },
  integration: { label: "Integration", Icon: Activity, cls: "text-indigo-500" },
};

function SourceCell({ source }: { source: string }) {
  const meta = SOURCE_META[source] ?? { label: source, Icon: AlertTriangle, cls: "text-gray-400" };
  const Icon = meta.Icon;
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 text-xs font-medium text-gray-600">
      <Icon size={12} className={meta.cls} aria-hidden="true" />
      {meta.label}
    </span>
  );
}

function detailText(event: HealthEvent): string | null {
  if (event.message) return event.message;
  const metric = event.details?.metric as string | undefined;
  const threshold = event.details?.threshold as number | string | undefined;
  if (metric && threshold !== undefined) return `${metric} · threshold ${String(threshold)}`;
  return metric ?? null;
}

export default function AlarmsTab({ platform }: { platform?: Platform }) {
  const [page, setPage] = useState(1);
  const [sourceFilter, setSourceFilter] = useState("");
  const [severityFilter, setSeverityFilter] = useState("");
  const [data, setData] = useState<HealthEventPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => { setPage(1); }, [sourceFilter, severityFilter, platform]);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const params: Parameters<typeof statusApi.getHealthEvents>[0] = { page, page_size: 25 };
        if (platform) params.platform = platform;
        if (sourceFilter) params.source = sourceFilter;
        const res = await statusApi.getHealthEvents(params);
        setData(res.data);
      } catch {
        setError("Failed to load alarms.");
      } finally {
        setLoading(false);
      }
    }, 200);
    return () => clearTimeout(debounceRef.current);
  }, [page, sourceFilter, platform]);

  const events = (data?.events ?? []).filter(
    (event) => !severityFilter || (event.severity ?? "") === severityFilter,
  );

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-center gap-3">
        <p className="mr-auto text-sm text-gray-500">Every raw signal, all severities — the firehose behind incidents.</p>
        <Select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)}>
          <option value="">All sources</option>
          <option value="cloudwatch">CloudWatch</option>
          <option value="signoz">SigNoz</option>
          <option value="uptimerobot">UptimeRobot</option>
          <option value="url">URL check</option>
          <option value="integration">Integration</option>
        </Select>
        <Select value={severityFilter} onChange={(e) => setSeverityFilter(e.target.value)}>
          <option value="">All severities</option>
          <option value="critical">Critical</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </Select>
      </div>

      {loading && !data ? (
        <LoadingText className="py-12 text-center">Loading alarms…</LoadingText>
      ) : error ? (
        <p className="py-12 text-center text-sm text-red-500">{error}</p>
      ) : events.length === 0 ? (
        <EmptyState
          bordered
          icon={<AlertTriangle size={28} aria-hidden="true" />}
          title="No alarms"
          description="Raw signals from CloudWatch, SigNoz, UptimeRobot and monitored URLs appear here as they arrive."
        />
      ) : (
        <>
          <div className="rounded-xl border border-gray-200 overflow-x-auto">
            <table className="w-full min-w-[760px] text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Severity</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Source</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Signal</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Detail</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Platform</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">When</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {events.map((event) => {
                  const detail = detailText(event);
                  return (
                    <tr key={event.id} className="hover:bg-gray-50/60">
                      <td className="px-4 py-3">
                        <SeverityBadge severity={event.severity ?? "info"} />
                      </td>
                      <td className="px-4 py-3">
                        <SourceCell source={event.source} />
                      </td>
                      <td className="px-4 py-3 font-medium text-gray-800">{event.component ?? "Signal"}</td>
                      <td className="px-4 py-3">
                        {detail ? (
                          <p className="max-w-[360px] truncate text-xs text-gray-500" title={detail}>{detail}</p>
                        ) : (
                          <span className="text-xs text-gray-300">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs font-medium text-gray-700 capitalize whitespace-nowrap">
                        {event.platform === "shared" ? "All" : event.platform}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                        {timeAgo(event.occurred_at) ?? formatDateTime(event.occurred_at)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {data && (
            <Pagination
              page={page}
              pageSize={data.page_size}
              total={data.total}
              onPageChange={setPage}
              label="Alarm pages"
            />
          )}
        </>
      )}
    </div>
  );
}
